// src/components/Dashboard/dashboardApi.js

// IMPORTANT: You will need to import fetchAuthSession from aws-amplify/auth
// Ensure you have `aws-amplify` installed and configured in your project (e.g., in index.js)
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Base URL for your API endpoints
const BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function now dynamically fetches the ID Token from AWS Amplify's Auth module.
 * It's crucial that a user is logged in for this to return a valid token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    // The ID Token is typically what API Gateway expects for authorization.
    if (tokens && tokens.idToken) {
      // Check if token is expired
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      console.log(
        'Available tokens:',
        tokens ? Object.keys(tokens) : 'No tokens object'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    console.log(
      'This usually means the user is not authenticated or there is a configuration issue.'
    );
    // If there's an error (e.g., no current authenticated user), return null
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Object>} - The JSON response from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken(); // Await the token retrieval

  if (!token) {
    // This will now be triggered if fetchAuthSession fails or returns no ID token
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${BASE_URL}${endpoint}`);
    const response = await fetch(`${BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Attempt to parse, but don't fail if not JSON
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      // Create a more informative error
      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_dashboard',
        `Dashboard API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[dashboardApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`Successfully received response from ${endpoint}:`, data);
      
      // Log API event for successful request
      loggingService.logInfo(
        'api_success_dashboard',
        `Successfully completed ${method} request to ${endpoint}`,
        {
          endpoint,
          method,
        }
      ).catch(logError => {
        console.warn('[dashboardApi] Failed to log success event:', logError);
      });
      
      return data;
    } else {
      // If response is not JSON, return an empty object or handle as needed
      console.warn(
        `API response for ${endpoint} was not JSON. Content-Type: ${contentType}`
      );
      
      // Log API event for non-JSON response
      loggingService.logInfo(
        'api_success_dashboard',
        `Successfully completed ${method} request to ${endpoint} (non-JSON response)`,
        {
          endpoint,
          method,
          contentType,
        }
      ).catch(logError => {
        console.warn('[dashboardApi] Failed to log success event:', logError);
      });
      
      return {}; // Or response.text() if you expect plain text
    }
  } catch (error) {
    // If it's our authentication error, re-throw it as-is
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    // Enhance the error with more context
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_dashboard',
      `Dashboard API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[dashboardApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for the Dashboard.
 */
export const dashboardApi = {
  /**
   * Fetches all sites accessible to the user.
   * GET /dashboard/site/getall
   */
  getAllSites: async () => {
    return makeApiRequest('/dashboard/site/getall');
  },

  /**
   * Fetches all employees with their current duty status.
   * GET /dashboard/user/get-all-with-duty-data
   */
  getAllUsersWithDutyData: async () => {
    return makeApiRequest('/dashboard/user/get-all-with-duty-data');
  },

  /**
   * Fetches assigned users for a specific site.
   * GET /dashboard/site/users?site_id={siteId}
   * @param {string} siteId - The ID of the site.
   */
  getAssignedUsersBySite: async siteId => {
    return makeApiRequest(`/dashboard/site/users?site_id=${siteId}`);
  },

  /**
   * Fetches assigned users for a specific site and shift (ss_id).
   * GET /dashboard/site/users/v2?site_id={siteId}&ss_id={ssId}
   * @param {string} siteId - The ID of the site.
   * @param {string} ssId - The shift schedule ID (optional).
   */
  getAssignedUsersBySiteAndShift: async (siteId, ssId = null) => {
    let url = `/dashboard/site/users/v2?site_id=${siteId}`;
    if (ssId) {
      url += `&ss_id=${ssId}`;
    }
    return makeApiRequest(url);
  },

  /**
   * Fetches the count of valid (scheduled) users.
   * GET /dashboard/count-valid
   */
  getValidUsersCount: async () => {
    return makeApiRequest('/dashboard/count-valid');
  },

  /**
   * Fetches the total count of non-admin users.
   * GET /dashboard/count-total-nadm
   */
  getTotalNonAdminCount: async () => {
    return makeApiRequest('/dashboard/count-total-nadm');
  },

  /**
   * Fetches the current location of a specific user.
   * GET /dashboard/get-user-loc?user_id={userId}
   * @param {string} userId - The ID of the user.
   */
  getUserLocation: async userId => {
    return makeApiRequest(`/dashboard/get-user-loc?user_id=${userId}`);
  },

  /**
   * Fetches a presigned URL for an S3 image path.
   * GET /s3-presigned?key={imagePath}
   * @param {string} imagePath - The S3 key (e.g., 's3://bucket-name/folder/image.jpg').
   * @returns {Promise<string>} - The presigned URL.
   */
  getPresignedS3Url: async imagePath => {
    // Ensure the imagePath is properly encoded if it contains special characters
    const encodedPath = encodeURIComponent(imagePath);
    const response = await makeApiRequest(`/s3-presigned?key=${encodedPath}`);
    // Assuming the response structure is { "presigned_url": "..." }
    if (response && response.presigned_url) {
      return response.presigned_url;
    }
    throw new Error('Failed to get presigned URL from API response.');
  },

  /**
   * Fetches site shifts for a specific site and date.
   * GET /dashboard/site/shifts?site_id={siteId}&date={date}
   * @param {string} siteId - The ID of the site.
   * @param {string} date - The date in YYYY-MM-DD format.
   */
  getSiteShifts: async (siteId, date) => {
    // Validate date format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error('Invalid date format. Use YYYY-MM-DD');
    }

    return makeApiRequest(
      `/dashboard/site/shifts?site_id=${siteId}&date=${date}`
    );
  },

  /**
   * Fetches a presigned URL for an S3 image path.
   * GET /user/profile-picture?user_id={userId}
   * @param {string} userId - The ID of the user.
   * @returns {Promise<string>} - The profile image URL.
   */
  getProfilePhoto: async userId => {
    const response = await makeApiRequest(`/user/profile-picture?user_id=${userId}`);
    // Assuming the response structure is { "profile_image_url": "..." }
    if (response && response.profile_image_url) {
      return response.profile_image_url;
    }
    throw new Error('Failed to get profile photo from API response.');
  },
};
